/**
 * Form system for Loan Drilldown SIDEBAR + MODAL forms — the tokens and sx
 * recipes behind FORM_STYLE_GUIDE.md. Read that doc before changing a value.
 *
 * Everything a form shows is one of these roles; nothing in a form component
 * types its own font size, weight, gap or color. Colors are the Eastview
 * palette from ./colors.js — never a hex here either.
 *
 * Usage:
 *   import { FORM, formSx, formLabel } from "../../theme/formStyles";
 *   <InputWrapper label={formLabel("Purchase price", { required: true })}
 *                 labelSx={formSx.label} helperSx={formSx.helper} …>
 *     <TextField sx={formSx.input} … />
 */
import { formFieldText, slate, status, teal } from "./colors";

/* ───────────────────────────── tokens ──────────────────────────────────── */

export const FORM = {
  /** Text roles: [fontSize px, lineHeight px, fontWeight]. */
  type: {
    title: [20, 26, 600], // modal title
    panelTitle: [18, 26, 600], // SIDEBAR panel title (§12b) — one step down
    section: [15, 20, 600], // section titles
    body: [14, 20, 400], // input text, doc names, button labels
    check: [13.5, 20, 500], // confirmation-row title (§5b)
    label: [13, 18, 500], // chips, quiet strip text, footer status
    desc: [13, 20, 400], // section description, footer status (§12b)
    cardTitle: [12.5, 17, 600], // method / choice card title (§4.1)
    helper: [12.5, 17, 400], // helper text — never italic
    /**
     * THE field label — 12/500 `ink2`, and nothing else in a form uses it.
     * It is deliberately a step under the 14px control text it sits above:
     * the value is the content, the label is the caption (§4).
     */
    inputLabel: [12, 16, 500],
    cardDesc: [11, 15, 400], // method / choice card description
    group: [12, 16, 600], // group labels inside a section ("Borrowers")
    meta: [12, 16, 400], // categories, counts, quiet right-hand text
    badge: [11.5, 15, 500], // the n/total section progress pill
    tag: [11, 14, 400], // the role suffix inside a chip, tiny pills
  },

  /**
   * Spacing, in px. `FORM.s.*` are the only gaps a form uses. The three that
   * carry the hierarchy: 6 (inside one field), 24 (field → field), 40
   * (section → section, with a hairline).
   */
  s: {
    glue: 6,
    s1: 4,
    s2: 8,
    s25: 10,
    s3: 12,
    s4: 16,
    s45: 20,
    s5: 24,
    s55: 28,
    s6: 32,
    s7: 40,
  },

  /** Shape & size (px). Inputs/buttons share the theme's CONTROL_RADIUS. */
  radius: { control: 8, modal: 16, checkbox: 4 },
  h: { input: 40, chip: 34, button: 38, row: 44, badge: 24, checkbox: 16 },

  /** Semantic color roles → palette tokens. */
  color: {
    accent: teal[600], // selection border, focus, links, primary action ONLY
    accentStrong: teal[700], // selected text, primary hover
    accentTint: teal[50], // selected-chip / badge fill
    accentWash: teal[100], // selected CARD fill, icon tile, complete badge
    accentBorder: teal[200], // selected-chip / badge border on a tint
    focusHalo: teal[200], // the 3px ring around a focused input
    ink: slate[900],
    ink2: slate[600],
    ink3: slate[500],
    ink4: slate[400],
    border: slate[300],
    divider: slate[200],
    surface: "#FFFFFF",
    surface2: slate[50],
    surface3: slate[100],
    warnBg: status.warning.bg,
    warnBorder: "#FDE68A",
    warnInk: status.warning.fg,
    required: status.error.fg,
  },
};

const t = (role, color) => {
  const [fontSize, lineHeight, fontWeight] = FORM.type[role];
  return { fontSize, lineHeight: `${lineHeight}px`, fontWeight, ...(color ? { color } : {}) };
};

/** px → the MUI spacing multiple `sx` wants (theme unit is 8px). */
export const px = (n) => `${n}px`;

/* ───────────────── field label + helper text (2026-10-08) ───────────────── */

/**
 * THE label / helper style for new forms (CLAUDE.md "Form field labels &
 * helpers"): label 12.5px / 500 #111827 with 4px to the control, helper 11.5px / 400 italic
 * #64748B.
 * First used on the Admin → Pricing "Add RTL tier" modal and the correspondent
 * Pricing tab cards.
 *
 * `label` is spread onto an `InputLabel` (InputWrapper's `labelSx`). The theme
 * styles `.MuiInputLabel-root:not(.MuiInputLabel-shrink)`, which out-specifies a
 * plain class, so the size + colour are repeated under that selector to win.
 * `helper` is spread onto InputWrapper's `FormHelperText` (`helperSx`), which
 * hard-codes `fontSize: 12px !important` — hence the `!important`. The
 * helper IS italic (weight 400) under this standard; the label is weight 500.
 */
const FIELD_LABEL_TEXT = {
  fontSize: 12.5,
  fontWeight: 500,
  lineHeight: 1.4,
  color: formFieldText.label,
};

export const FIELD_TEXT = {
  label: {
    ...FIELD_LABEL_TEXT,
    marginBottom: "4px",
    whiteSpace: "normal",
    "&.MuiInputLabel-root:not(.MuiInputLabel-shrink)": FIELD_LABEL_TEXT,
  },
  helper: {
    fontSize: "11.5px !important",
    lineHeight: 1.45,
    color: formFieldText.helper,
    fontWeight: 400,
    fontStyle: "italic",
    mt: "4px",
    mx: 0,
  },
  /** Field rows are 24px apart; columns keep the grid's own gap. */
  grid: { rowGap: "24px" },
};

/* ─────────────────────────── sx recipes ────────────────────────────────── */

export const formSx = {
  /* text roles */
  title: { ...t("title", FORM.color.ink), letterSpacing: "-0.01em", display: "block" },
  titleHelper: { ...t("helper", FORM.color.ink3), display: "block", mt: px(2) },
  sectionTitle: { ...t("section", FORM.color.ink), display: "block" },
  sectionHelper: { ...t("desc", FORM.color.ink3), display: "block", mt: px(FORM.s.glue), maxWidth: "60ch" },
  body: t("body", FORM.color.ink),
  /**
   * THE field label. The nested selector is not decoration: the MUI theme
   * styles `.MuiInputLabel-root:not(.MuiInputLabel-shrink)` (weight 400, muted
   * grey) so a RESTING floating label reads as a placeholder — and that
   * `:not()` out-specifies a plain `sx` class, which silently ate the form's
   * weight and color. Our labels never float (label-above is the house rule),
   * so we match that selector at higher specificity and win outright.
   */
  label: {
    ...t("inputLabel", FORM.color.ink2),
    marginBottom: px(FORM.s.glue),
    whiteSpace: "normal",
    "&.MuiInputLabel-root:not(.MuiInputLabel-shrink)": t("inputLabel", FORM.color.ink2),
  },
  /** "(optional)" after a label — same size, regular weight, quiet. */
  labelOptional: { ...t("inputLabel", FORM.color.ink3), fontWeight: 400, ml: px(4) },
  helper: { ...t("helper", FORM.color.ink3), fontStyle: "normal", mt: px(FORM.s.glue), mx: 0 },
  groupLabel: { ...t("group", FORM.color.ink2), display: "block", mb: px(FORM.s.s2) },
  meta: t("meta", FORM.color.ink3),
  required: { color: FORM.color.required, fontWeight: 500, ml: px(3) },
  /** Currency / date values: fixed-width digits so columns line up in review. */
  money: { fontVariantNumeric: "tabular-nums" },

  /* layout */
  /** Stack of fields inside a section — 24px apart. */
  fields: { display: "flex", flexDirection: "column", gap: px(FORM.s.s5) },
  /** Fields indent to the section TITLE (badge + gap), not the badge. */
  sectionBody: { pl: { xs: 0, sm: px(FORM.h.badge + FORM.s.s3) } },
  grid2: { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: px(FORM.s.s5) },
  grid3: { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: px(FORM.s.s5) },
  /** Hairline between sections — 20px above + 20px below = the 40px section gap. */
  divider: { my: px(FORM.s.s7 / 2), borderColor: FORM.color.divider },

  /* section header: badge + title on one line, helper on the next */
  /**
   * Section head. It carries NO bottom margin: the gap to the body is 24px
   * open and **0 collapsed**, which only `FormSection` knows, so it sets it
   * (`formSx.sectionHeadGap(open)`). A folded section then ends flush at its
   * title rather than leaving a gap floating above the divider.
   */
  sectionHead: { display: "flex", alignItems: "flex-start", gap: px(FORM.s.s3) },
  sectionHeadGap: (open) => ({ marginBottom: open ? px(FORM.s.s5) : 0 }),
  /** The n/total progress pill beside a section title; brand once complete. */
  sectionCount: (complete) => ({
    ...t("badge", complete ? FORM.color.accentStrong : FORM.color.ink3),
    bgcolor: complete ? FORM.color.accentWash : FORM.color.surface3,
    borderRadius: px(999),
    px: px(FORM.s.s2),
    py: px(2),
    flexShrink: 0,
    fontVariantNumeric: "tabular-nums",
  }),
  sectionBadge: {
    width: FORM.h.badge,
    height: FORM.h.badge,
    borderRadius: "50%",
    bgcolor: FORM.color.accentTint,
    border: `1px solid ${FORM.color.accentBorder}`,
    color: FORM.color.accentStrong,
    ...t("meta"),
    fontWeight: 600,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
  },

  /* controls */
  /** Text field / dollar field: 40px, 14px body text, tabular digits. */
  input: {
    "& .MuiOutlinedInput-root": { height: FORM.h.input, fontSize: FORM.type.body[0], borderRadius: px(FORM.radius.control) },
    "& .MuiOutlinedInput-input": { fontSize: FORM.type.body[0], color: FORM.color.ink, fontVariantNumeric: "tabular-nums", py: 0 },
    "& .MuiOutlinedInput-input::placeholder": { color: FORM.color.ink4, opacity: 1 },
    "& .MuiInputAdornment-root": { color: FORM.color.ink4 },
    // Focus reads as a ring, not a thicker line: accent border + 3px halo.
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: `0 0 0 3px ${FORM.color.focusHalo}`,
      "& .MuiOutlinedInput-notchedOutline": { borderWidth: 1, borderColor: FORM.color.accent },
    },
    "& .MuiOutlinedInput-root:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": {
      borderColor: FORM.color.ink4,
    },
    // Native date/number chrome — quiet the picker glyph, drop the spinners.
    "& input[type=date]::-webkit-calendar-picker-indicator": { opacity: 0.55, cursor: "pointer" },
    "& input[type=number]": { MozAppearance: "textfield" },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  },
  /** A quiet unit after a value — "years", "projects". */
  inputSuffix: { ...t("label", FORM.color.ink3), fontWeight: 400 },
  /** One toggle chip for single- AND multi-select; selection = border + tint + text together. */
  chip: (on) => ({
    height: FORM.h.chip,
    px: px(2),
    borderRadius: px(FORM.h.chip / 2),
    ...t("label"),
    bgcolor: on ? FORM.color.accentTint : FORM.color.surface,
    color: on ? FORM.color.accentStrong : FORM.color.ink2,
    border: `1px solid ${on ? FORM.color.accent : FORM.color.border}`,
    transition: "background .12s, border-color .12s, color .12s",
    "& .MuiChip-label": { px: px(12), display: "flex", alignItems: "center", gap: px(6) },
    "& .MuiChip-icon": { color: FORM.color.accent, fontSize: 15, ml: px(10), mr: px(-4) },
    "&:hover": { bgcolor: on ? FORM.color.accentTint : FORM.color.surface2, borderColor: on ? FORM.color.accent : FORM.color.ink4 },
    "&.Mui-focusVisible": { outline: `2px solid ${FORM.color.accent}`, outlineOffset: 2 },
  }),
  /** The role suffix inside a chip ("Borrower", "GC"). */
  chipTag: (on) => ({ ...t("tag"), color: on ? FORM.color.accent : FORM.color.ink4 }),
  chipRow: { display: "flex", flexWrap: "wrap", gap: px(FORM.s.s2) },
  checkbox: {
    p: 0,
    color: FORM.color.border,
    "& .MuiSvgIcon-root": { fontSize: 18 },
    "&.Mui-checked": { color: FORM.color.accent },
  },

  /* messages */
  /** Quiet empty-state / "nothing here yet" note. */
  note: {
    ...t("label", FORM.color.ink3),
    fontWeight: 400,
    bgcolor: FORM.color.surface2,
    border: `1px dashed ${FORM.color.border}`,
    borderRadius: px(FORM.radius.control),
    px: px(FORM.s.s4),
    py: px(FORM.s.s3),
  },
  /** Amber advisory — the one warning style. Copy: bold problem · consequence · imperative fix. */
  warn: {
    display: "flex",
    alignItems: "flex-start",
    gap: px(FORM.s.s3),
    bgcolor: FORM.color.warnBg,
    border: `1px solid ${FORM.color.warnBorder}`,
    borderRadius: px(FORM.radius.control),
    px: px(FORM.s.s4),
    py: px(FORM.s.s3),
    ...t("label", FORM.color.warnInk),
    fontWeight: 400,
    "& strong": { fontWeight: 600 },
    "& .MuiSvgIcon-root": { fontSize: 18, flexShrink: 0, mt: px(1) },
  },
  /** Teal "how this works" callout — context the reader should not skip, but
   *  not a problem (that is `warn`). Same anatomy as `warn`. */
  info: {
    display: "flex",
    alignItems: "flex-start",
    gap: px(FORM.s.s3),
    bgcolor: teal[50],
    border: `1px solid ${teal[200]}`,
    borderRadius: px(FORM.radius.control),
    px: px(FORM.s.s4),
    py: px(FORM.s.s3),
    ...t("label", teal[800]),
    fontWeight: 400,
    "& strong": { fontWeight: 600 },
    "& .MuiSvgIcon-root": {
      fontSize: 18,
      flexShrink: 0,
      mt: px(1),
      color: teal[600],
    },
  },

  /* grouped list (documents, rows of records) */
  listGroup: { border: `1px solid ${FORM.color.divider}`, borderRadius: px(FORM.radius.control), overflow: "hidden" },
  listGroupHead: {
    display: "flex",
    alignItems: "baseline",
    gap: px(FORM.s.s2),
    px: px(FORM.s.s4),
    py: px(FORM.s.s2),
    bgcolor: FORM.color.surface2,
    borderBottom: `1px solid ${FORM.color.divider}`,
    ...t("group", FORM.color.ink2),
  },
  listGroupCount: { ...t("meta", FORM.color.ink4) },
  listGroupPill: {
    ml: "auto",
    ...t("tag", FORM.color.accentStrong),
    fontWeight: 500,
    bgcolor: FORM.color.accentTint,
    border: `1px solid ${FORM.color.accentBorder}`,
    px: px(8),
    py: px(1),
    borderRadius: px(10),
  },
  listRow: (on) => ({
    display: "flex",
    alignItems: "center",
    gap: px(FORM.s.s3),
    minHeight: FORM.h.row,
    px: px(FORM.s.s4),
    cursor: "pointer",
    bgcolor: on ? FORM.color.accentTint : FORM.color.surface,
    transition: "background .1s",
    "& + &": { borderTop: `1px solid ${FORM.color.divider}` },
    "&:hover": { bgcolor: on ? FORM.color.accentTint : FORM.color.surface2 },
  }),
  listRowName: { ...t("body", FORM.color.ink), flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  listRowMeta: { ...t("meta", FORM.color.ink3), textAlign: "right", flexShrink: 0, display: { xs: "none", sm: "block" } },

  /**
   * File drop zone (§10b) — the upload control for a form whose FILE is the
   * payload (Add Sizer), as opposed to §10's "attach from inside the form",
   * which hands off to a `*FileUploadProcess`. Dashed like `note` at rest;
   * takes the selection language (accent border + tint) while a file is
   * dragged over it. It is a real <button>, so it resets the UA font/width.
   */
  drop: (over) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: px(FORM.s.s2),
    width: "100%",
    minHeight: 128,
    px: px(FORM.s.s4),
    py: px(FORM.s.s5),
    font: "inherit",
    textAlign: "center",
    border: `1px dashed ${over ? FORM.color.accent : FORM.color.border}`,
    borderRadius: px(FORM.radius.control),
    bgcolor: over ? FORM.color.accentTint : FORM.color.surface2,
    cursor: "pointer",
    transition: "border-color .15s, background .15s",
    "&:hover": { borderColor: FORM.color.accent },
    "&:focus-visible": { outline: `2px solid ${FORM.color.accent}`, outlineOffset: 2 },
  }),
  dropIcon: (over) => ({
    width: 36,
    height: 36,
    borderRadius: px(FORM.radius.control),
    display: "grid",
    placeItems: "center",
    bgcolor: over ? FORM.color.surface : FORM.color.surface3,
    color: over ? FORM.color.accent : FORM.color.ink2,
    "& .MuiSvgIcon-root": { fontSize: 18 },
  }),
  /** "Drop the file here or <strong>browse</strong>" — the strong is the link. */
  dropText: { ...t("label", FORM.color.ink2), fontWeight: 400, display: "block", "& strong": { fontWeight: 500, color: FORM.color.accent } },
  dropHint: { ...t("meta", FORM.color.ink3), display: "block" },
  /** A picked file: a static 44px row inside `listGroup` — name previews, × removes. */
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: px(FORM.s.s3),
    minHeight: FORM.h.row,
    pl: px(FORM.s.s4),
    pr: px(FORM.s.s1),
    bgcolor: FORM.color.surface,
    "& + &": { borderTop: `1px solid ${FORM.color.divider}` },
    "& > .MuiSvgIcon-root": { fontSize: 18, color: FORM.color.ink3, flexShrink: 0 },
  },

  /**
   * A selectable CARD — for choices that carry data the user compares before
   * picking (a unit with its rents, a rent basis with its dollar figure, a
   * property). Selection = the chip language (accent border + tint); the card
   * body is free-form but its text uses the label / meta roles.
   */
  choice: (on, disabled) => ({
    border: `1px solid ${on ? FORM.color.accent : FORM.color.divider}`,
    bgcolor: on ? FORM.color.accentTint : disabled ? FORM.color.surface2 : FORM.color.surface,
    borderRadius: px(FORM.radius.control),
    px: px(FORM.s.s4),
    py: px(FORM.s.s3),
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    transition: "border-color .12s, background .12s",
    "&:hover": disabled || on ? {} : { borderColor: FORM.color.ink4, bgcolor: FORM.color.surface2 },
    "&:focus-visible": { outline: `2px solid ${FORM.color.accent}`, outlineOffset: 2 },
  }),
  choiceGrid: { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(180px, 1fr))" }, gap: px(FORM.s.s2) },
  /** A tiny status pill inside a card or row ("Vacant", "has rent row"). */
  pill: (tone) => ({
    height: 18,
    ...t("tag"),
    fontWeight: 600,
    px: px(8),
    borderRadius: px(9),
    display: "inline-flex",
    alignItems: "center",
    bgcolor: (status[tone] || status.neutral).bg,
    color: (status[tone] || status.neutral).fg,
  }),
  /** The live "counts as" strip: entered → monthly → after haircut. */
  strip: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: px(FORM.s.s2),
    px: px(FORM.s.s4),
    py: px(FORM.s.s2),
    bgcolor: FORM.color.surface2,
    border: `1px solid ${FORM.color.divider}`,
    borderRadius: px(FORM.radius.control),
    ...t("helper", FORM.color.ink3),
    "& b": { ...t("label", FORM.color.ink), fontVariantNumeric: "tabular-nums" },
  },

  /* buttons — height / type only; color stays with the theme variant */
  button: { height: FORM.h.button, px: px(FORM.s.s4), borderRadius: px(FORM.radius.control), ...t("body"), fontWeight: 500 },
  buttonRow: { display: "flex", gap: px(FORM.s.s2), ml: "auto", flexShrink: 0 },

  /* modal / sidebar shell */
  paper: { borderRadius: px(FORM.radius.modal), maxWidth: 880 },
  head: { px: px(FORM.s.s6), pt: px(FORM.s.s5), pb: px(FORM.s.s4), borderBottom: `1px solid ${FORM.color.divider}` },
  /**
   * CONFIRMATION variant of the modal shell. A confirmation is one question,
   * not a form with regions, so it differs from `head` / `shellBody` twice:
   * the title drops a step to the `panelTitle` role (18/600), and the header
   * carries NO hairline — there are no regions for a rule to separate, and a
   * line above a single sentence reads as a heavier object than the decision
   * deserves. The footer keeps its rule: that one separates the question from
   * the actions. `confirmBody` has no top padding because `confirmHead`'s
   * 12px bottom is the whole title→message gap once the rule is gone.
   */
  confirmHead: { px: px(FORM.s.s6), pt: px(FORM.s.s5), pb: px(FORM.s.s3) },
  confirmTitle: { ...t("panelTitle", FORM.color.ink), letterSpacing: "-0.01em", display: "block" },
  confirmBody: { px: px(FORM.s.s6), pt: 0, pb: px(FORM.s.s6) },
  /**
   * QUICK-EDIT variant of the modal shell (§12c) — ONE or TWO inputs and
   * nothing else: change a date, rename a thing, set an amount. It is the
   * confirmation modal's sibling, with a control where the sentence was, so
   * it borrows the same two decisions — the title drops to `panelTitle`
   * (18/600) and the header carries **no hairline**, because there are still
   * no regions for a rule to separate. The footer keeps its rule: that one
   * separates the input from the actions.
   *
   * Everything else is narrower than the full modal: 24px sides instead of
   * 32, a 460px cap instead of 880. A form this small in an 880px paper reads
   * as a page with one field on it.
   *
   * What it deliberately does NOT carry (and what makes it the wrong shell if
   * you need them): sections, an eyebrow / record line above the title, a
   * "current value" echo, an audit-note field, a provenance byline. Those
   * belong to the full §12a modal.
   */
  quickPaper: { borderRadius: px(FORM.radius.modal), maxWidth: 460 },
  quickHead: { px: px(FORM.s.s5), pt: px(FORM.s.s45), pb: px(FORM.s.s4) },
  quickTitle: { ...t("panelTitle", FORM.color.ink), letterSpacing: "-0.01em", display: "block" },
  quickBody: { px: px(FORM.s.s5), pt: 0, pb: px(FORM.s.s5) },
  /**
   * Field → field at 20px rather than §4's 24: with at most two fields and no
   * section above them, 24 opens a gap the modal isn't tall enough to carry.
   */
  quickFields: { display: "flex", flexDirection: "column", gap: px(FORM.s.s45) },
  /**
   * ONE input: its label is hidden, because the modal's title already names
   * the thing being changed and "Edit scheduled closing date" over a field
   * captioned "Scheduled closing date" says it twice.
   *
   * Hidden VISUALLY, not removed: the label stays in the DOM, clipped, right
   * before its control, so a screen reader still reads it. `position:
   * absolute` also takes it out of flow, so the 6px label→control glue
   * collapses and the control sits straight under the header.
   */
  quickHiddenLabel: {
    "& .MuiInputLabel-root": {
      position: "absolute",
      width: 1,
      height: 1,
      p: 0,
      m: 0,
      overflow: "hidden",
      clip: "rect(0 0 0 0)",
      clipPath: "inset(50%)",
      whiteSpace: "nowrap",
      border: 0,
    },
  },
  quickFoot: {
    borderTop: `1px solid ${FORM.color.divider}`,
    px: px(FORM.s.s5),
    py: px(FORM.s.s3),
    display: "flex",
    alignItems: "center",
    gap: px(FORM.s.s3),
  },
  /** The ⏎ cap inside the primary button — Enter submits, so the button says so. */
  quickEnterKey: {
    ...t("tag"),
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 16,
    height: 16,
    ml: px(FORM.s.s2),
    px: px(3),
    borderRadius: px(4),
    bgcolor: "rgba(255,255,255,.22)",
    lineHeight: 1,
  },
  shellBody: { px: px(FORM.s.s6), py: px(FORM.s.s6) },
  foot: {
    borderTop: `1px solid ${FORM.color.divider}`,
    px: px(FORM.s.s6),
    py: px(FORM.s.s4),
    display: "flex",
    alignItems: "center",
    gap: px(FORM.s.s4),
    flexWrap: "wrap",
  },
  footStatus: { ...t("label", FORM.color.ink3), fontWeight: 400, lineHeight: 1.5, flex: 1, minWidth: 220 },
  /**
   * Sidebar variant of the footer — `DrilldownSidebarPane` has no footer slot,
   * so the form renders its own, stuck to the bottom of the scrolling body.
   * Same anatomy as `foot`; the sidebar is narrower, so 24px sides.
   */
  stickyFoot: {
    position: "sticky",
    bottom: 0,
    zIndex: 1,
    bgcolor: FORM.color.surface,
    borderTop: `1px solid ${FORM.color.divider}`,
    mx: px(-FORM.s.s5),
    mb: px(-FORM.s.s5),
    px: px(FORM.s.s5),
    py: px(FORM.s.s3),
    display: "flex",
    alignItems: "center",
    gap: px(FORM.s.s3),
    flexWrap: "wrap",
  },
  /**
   * Sidebar header — pass as `DrilldownSidebarPane`'s `headerSx`. The pane's
   * own title is a 20px bold `h1`; this pulls it onto the form scale (title
   * 20/600 ink, tight tracking) and gives the header the modal's padding +
   * hairline. Pass the description as `descriptionContent={<Txt sx=
   * {formSx.titleHelper}>…</Txt>}` so it wears the helper role too.
   */
  sidebarHead: {
    px: px(FORM.s.s5),
    pt: px(FORM.s.s5),
    pb: px(FORM.s.s4),
    borderBottom: `1px solid ${FORM.color.divider}`,
    "& .MuiTypography-h1": { ...t("title", FORM.color.ink), letterSpacing: "-0.01em", fontSize: FORM.type.title[0] },
  },
  /** Sidebar body — 24px sides (the pane is ~680px), 32px top, room for the footer. */
  sidebarBody: { px: px(FORM.s.s5), pt: px(FORM.s.s6), pb: px(FORM.s.s5) },
  link: { ...t("helper"), fontWeight: 500, color: FORM.color.accent, verticalAlign: "baseline" },

  /* ══════════════ panel form (§12b) — the SIDEBAR form shell ═════════════
   * Header and footer are pinned; only the body scrolls. Breakpoints are
   * container queries on the PANEL, not the viewport, so dragging the resize
   * handle re-lays the form out the same way a narrow window would.
   * `panelContainer` goes on the pane's `sx` — it is what the @container
   * rules below resolve against.
   */
  panelContainer: { containerType: "inline-size" },

  /** Longhand `padding` for the same reason as `panelBody` below. */
  panelHead: {
    padding: `${px(FORM.s.s45)} ${px(FORM.s.s55)} ${px(FORM.s.s45 - 2)}`,
    borderBottom: `1px solid ${FORM.color.divider}`,
    "& .MuiTypography-h1": {
      ...t("panelTitle", FORM.color.ink),
      letterSpacing: "-0.01em",
      fontSize: FORM.type.panelTitle[0],
    },
    "@container (max-width: 520px)": { px: px(18) },
  },
  /** The 36px brand tile the panel title sits beside. */
  panelHeadIcon: {
    width: 36,
    height: 36,
    borderRadius: px(FORM.radius.control),
    bgcolor: FORM.color.accentWash,
    color: FORM.color.accent,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    "& .MuiSvgIcon-root": { fontSize: 18 },
  },
  /**
   * 24 top · 28 sides · 40 bottom, as the `padding` LONGHAND on purpose.
   * `ResizeableSidebar`'s body already carries `px` / `py` / `p` keys; a `px`
   * here would overwrite that `px` **in its original position** (JS keeps an
   * existing key's insertion order through a spread), landing it before the
   * body's `p: bodyPadding` — which would then win and flatten the padding to
   * zero. A key the body does not already have can't be re-ordered like that.
   */
  panelBody: {
    padding: `${px(FORM.s.s5)} ${px(FORM.s.s55)} ${px(FORM.s.s7)}`,
    "@container (max-width: 520px)": {
      padding: `${px(FORM.s.s45)} ${px(18)} ${px(FORM.s.s6)}`,
    },
    "& > *": { maxWidth: 980, mx: "auto" },
  },
  panelFoot: {
    display: "flex",
    alignItems: "center",
    gap: px(FORM.s.s3),
    px: px(FORM.s.s55),
    py: px(14),
    borderTop: `1px solid ${FORM.color.divider}`,
    bgcolor: FORM.color.surface,
    "@container (max-width: 520px)": { px: px(18) },
  },
  /** Footer status: a 96px track + "N required fields left". */
  progressRow: { display: "flex", alignItems: "center", gap: px(FORM.s.s25), flex: 1, minWidth: 0 },
  progressTrack: {
    width: 96,
    height: 4,
    borderRadius: px(4),
    bgcolor: FORM.color.surface3,
    flexShrink: 0,
    overflow: "hidden",
  },
  progressFill: (pct) => ({
    width: `${pct}%`,
    height: "100%",
    bgcolor: FORM.color.accent,
    transition: "width .25s",
  }),
  progressText: {
    ...t("desc", FORM.color.ink3),
    "@container (max-width: 520px)": { display: "none" },
  },

  /* ── section block: 28 above + hairline + 28 below ── */
  panelSection: {
    py: px(FORM.s.s6),
    borderTop: `1px solid ${FORM.color.divider}`,
    "&:first-of-type": { borderTop: 0, pt: 0 },
    "&:last-of-type": { pb: 0 },
    "@container (max-width: 760px)": { py: px(FORM.s.s55) },
  },

  /**
   * The 6-column field grid (§4.3): full 6 · half 3 · third 2.
   *
   * **A child with no span is FULL WIDTH, not one sixth.** CSS would give an
   * un-spanned grid item a single column, so a field whose control forgot to
   * forward `span` would silently render at 1/6 — narrow enough to look like a
   * styling bug and wide enough to ship. The `& > *` baseline makes the safe
   * thing the default; `fieldSpan` overrides it from `&&` (specificity 0,2,0)
   * so the two can't fight over stylesheet order.
   */
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    rowGap: px(FORM.s.s45),
    columnGap: px(FORM.s.s4),
    "& > *": { gridColumn: "span 6", minWidth: 0 },
    "@container (max-width: 520px)": {
      gridTemplateColumns: "minmax(0, 1fr)",
      "& > *": { gridColumn: "span 1" },
    },
  },
  fieldSpan: (n) => ({
    minWidth: 0,
    "&&": { gridColumn: `span ${n}` },
    "@container (max-width: 520px)": { "&&": { gridColumn: "span 1" } },
  }),

  /* ── Yes / No segmented control (§5) ── */
  segTrack: {
    display: "inline-flex",
    gap: px(2),
    p: px(3),
    bgcolor: FORM.color.surface3,
    borderRadius: px(FORM.radius.control),
    flexShrink: 0,
  },
  segOption: (on, accent) => ({
    px: px(18),
    py: px(FORM.s.glue),
    borderRadius: px(6),
    border: 0,
    cursor: "pointer",
    fontFamily: "inherit",
    ...t("label"),
    color: on ? (accent ? FORM.color.accentStrong : FORM.color.ink) : FORM.color.ink2,
    bgcolor: on ? FORM.color.surface : "transparent",
    boxShadow: on ? "0 1px 2px rgba(15,25,35,.12)" : "none",
    transition: "background .15s, color .15s",
    "&:hover": { color: FORM.color.ink },
    "&:focus-visible": { outline: `2px solid ${FORM.color.accent}`, outlineOffset: 2 },
  }),

  /* ── confirmation list (§5b): titled rows, control on the right ── */
  checkList: {
    border: `1px solid ${FORM.color.divider}`,
    borderRadius: px(FORM.radius.modal - 4),
    overflow: "hidden",
  },
  checkRow: {
    display: "flex",
    alignItems: "center",
    gap: px(FORM.s.s4),
    px: px(FORM.s.s4),
    py: px(FORM.s.s3),
    minHeight: FORM.h.row,
    "& + &": { borderTop: `1px solid ${FORM.color.surface3}` },
    "@container (max-width: 520px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: px(FORM.s.s2),
    },
  },
  checkRowTitle: { ...t("check", FORM.color.ink), display: "block" },
  checkRowSub: { ...t("meta", FORM.color.ink3), display: "block", mt: px(1) },

  /* ── method selector cards (§4.1) ── */
  methodQuestion: { ...t("desc", FORM.color.ink2), fontWeight: 500, display: "block", mb: px(FORM.s.s25) },
  /**
   * One track per card, so two options fill the row the same way three do.
   * A fixed `repeat(3, …)` left a two-card chooser sitting in two thirds of
   * the panel with an empty column beside it. Capped at 3 — past that the
   * cards get too narrow for their description to read, and they wrap.
   */
  methodGrid: (count = 3) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${Math.min(Math.max(count, 1), 3)}, minmax(0, 1fr))`,
    gap: px(FORM.s.s25),
    "@container (max-width: 760px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  }),
  methodCard: (on) => ({
    position: "relative",
    display: "flex",
    gap: px(FORM.s.s3),
    p: px(14),
    pr: px(34),
    textAlign: "left",
    border: `1px solid ${on ? FORM.color.accent : FORM.color.divider}`,
    borderRadius: px(FORM.radius.modal - 4),
    bgcolor: on ? FORM.color.accentTint : FORM.color.surface,
    boxShadow: on ? `inset 0 0 0 1px ${FORM.color.accent}` : "none",
    cursor: "pointer",
    transition: "border-color .15s, background .15s",
    "&:hover": on ? {} : { borderColor: FORM.color.border, bgcolor: FORM.color.surface2 },
    "&:focus-visible": { outline: `2px solid ${FORM.color.accent}`, outlineOffset: 2 },
  }),
  methodIcon: (on) => ({
    width: 32,
    height: 32,
    borderRadius: px(FORM.radius.control),
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    bgcolor: on ? FORM.color.surface : FORM.color.surface3,
    color: on ? FORM.color.accent : FORM.color.ink2,
    "& .MuiSvgIcon-root": { fontSize: 16 },
  }),
  methodRadio: (on) => ({
    position: "absolute",
    top: px(FORM.s.s3),
    right: px(FORM.s.s3),
    width: 16,
    height: 16,
    borderRadius: "50%",
    border: `1.5px solid ${on ? FORM.color.accent : FORM.color.border}`,
    bgcolor: on ? FORM.color.accent : "transparent",
    boxShadow: on ? `inset 0 0 0 3.5px ${FORM.color.surface}` : "none",
  }),
  /** `layout="inline"`: header row (icon + title), description under it, no radio. */
  methodCardInline: { flexDirection: "column", gap: px(FORM.s.s2), pr: px(14) },
  methodIconInline: {
    width: 26,
    height: 26,
    borderRadius: px(FORM.radius.control),
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    bgcolor: FORM.color.surface3,
    color: FORM.color.ink2,
    "& .MuiSvgIcon-root": { fontSize: 15 },
  },
  methodTitle: { ...t("cardTitle", FORM.color.ink), display: "block" },
  methodDesc: { ...t("cardDesc", FORM.color.ink3), display: "block", mt: px(2) },
};

/* ─────────────────────────── label helper ──────────────────────────────── */

/**
 * A field label with its required marker TOUCHING the text: `Purchase price *`.
 * Optional fields carry no marker. Pass the result as `InputWrapper`'s `label`
 * (do not also pass `required` — the marker comes from here so it can carry
 * the form system's color and never floats to its own line).
 */
export const formLabel = (text, { required = false } = {}) => (
  <>
    {text}
    {required ? (
      <span style={{ color: FORM.color.required, fontWeight: 500, marginLeft: 3 }} aria-hidden="true">
        *
      </span>
    ) : null}
  </>
);
